"use client";

import * as React from "react";
import { residentialConfig, businessConfig } from "@/components/signup/signup-content";
import { applyWmsPricing, type MembershipPricing } from "@/lib/membership-pricing";
import { usePathname } from "next/navigation";
import { PricingModal } from "./PricingModal";
import { IdentityConsentModal } from "./IdentityConsentModal";
import { isPremiumOnly } from "./gate-config";
import { PreferencesModal } from "../PreferencesModal";

type Plan = "monthly" | "yearly" | "standard" | "premium";

type GateValue = {
  /** True when the member is on the Basic plan (not active Premium). */
  locked: boolean;
  /** Open the pricing popup (called by the "Upgrade to Premium" CTAs). */
  openPricing: () => void;
  /** A membership popup is on screen — other one-time notices wait their turn. */
  upsellOpen: boolean;
  /** Open the preferences popup (the FeatureLock CTA reopens it after a dismissal). */
  openPreferences: () => void;
  /** The member has not told us what to look at for them. */
  preferencesMissing: boolean;
};

const MembershipGateContext = React.createContext<GateValue>({
  locked: false,
  openPricing: () => {},
  upsellOpen: false,
  openPreferences: () => {},
  preferencesMissing: false,
});

/** Remembers which login has already been shown the membership page (one key, never grows). */
const SHOWN_KEY = "cjowl.membership.shown-for-login";

export function useMembershipGate(): GateValue {
  return React.useContext(MembershipGateContext);
}

/**
 * Wraps the CJ Owl dashboard (freemium). Basic members (`locked`) see the membership page FIRST — once per sign-in
 * (client, 2026-09-18: "After Customer Complete account creation and redirects to dashboard this should be the 1st
 * page … For Basic member every login 1st page should be this"). They can close it and use the dashboard freely; it
 * does not come back until their next login, and the "Upgrade to Premium" CTAs (FeatureLock, the sidebar Basic Plan
 * card) still open it on demand through openPricing().
 *
 * Premium-only pages render <FeatureLock> in place (see the dashboard layout). Choosing a plan advances to the
 * Identity & Consent popup → Stripe checkout. Active-Premium members see no popups (locked=false) and the gate is a
 * passthrough. An agent PREVIEWING a customer's dashboard never gets it — it is not their membership to buy.
 */
export function MembershipGate({
  locked,
  variant,
  memberName,
  pricing,
  loginAt,
  preview = false,
  preferencesCompleted = true,
  preferencesShouldPrompt = false,
  children,
}: {
  locked: boolean;
  variant: "residential" | "business";
  memberName: string;
  pricing?: MembershipPricing | null;
  /** When this sign-in happened (member cookie). One showing per login is keyed on it. */
  loginAt?: number | null;
  /** An agent looking at the customer's dashboard — never upsell them. */
  preview?: boolean;
  /** Has the member told us what to look at for them? Defaults TRUE so an unreadable snapshot
   *  never nags somebody who has already answered. */
  preferencesCompleted?: boolean;
  /** The CRM's cadence answer: is an unprompted ask due? Separate from the fact above — see below. */
  preferencesShouldPrompt?: boolean;
  children: React.ReactNode;
}) {
  // WMS-sourced prices (passed from the server layout) override the static config.
  const config = applyWmsPricing(variant === "business" ? businessConfig : residentialConfig, pricing ?? null);
  const [pricingOpen, setPricingOpen] = React.useState(false);
  const [identityOpen, setIdentityOpen] = React.useState(false);
  // Default to the config's highlighted plan (the Premium tier for both audiences).
  const [plan, setPlan] = React.useState<Plan>(
    () => (config.plans.find((p) => p.badge)?.value ?? config.plans[0]?.value ?? "premium") as Plan,
  );

  // ── What to ask for, and in what order ──────────────────────────────────────────────────────
  //
  // 🔴 DECIDED AT RENDER, FROM PROPS — never from a child's effect. The first attempt had
  // `PreferencesPrompt` take a "hold" on the upsell from inside the page, and it did not work on
  // the only load that matters. React flushes a child's effects BEFORE its parent's in the same
  // commit, so the gate's effect still read holds === 0, stamped SHOWN_KEY and opened the pricing
  // modal; the re-render then had holds === 1 AND pricingOpen === true, the effect bailed, and
  // nothing closed what it had already opened. The pricing modal covered the preferences card on
  // the first visit of every login — the exact failure it was written to prevent, invisible from
  // the second page load because SHOWN_KEY was by then stamped.
  //
  // One owner removes the race, and incidentally lets the preferences popup render on EVERY
  // dashboard route instead of only the overview — which is what the locked-item ask needs.
  const pathname = usePathname() ?? "";
  const onLockedPage = locked && isPremiumOnly(pathname);

  // Answering is optimistic: the snapshot is a server prop and will not change until the next
  // navigation, but the member must not be asked again the moment they save.
  const [answered, setAnswered] = React.useState(false);
  // Dismissal is remembered PER PATH, so closing it does not silence the next locked page the
  // member opens — "they'll be asked again when trying to navigate locked items" (client).
  const [dismissedFor, setDismissedFor] = React.useState<string | null>(null);

  const preferencesMissing = !preferencesCompleted && !answered;
  const prefsOpen =
    !preview &&
    preferencesMissing &&
    dismissedFor !== pathname &&
    // 🔴 TWO DIFFERENT SIGNALS, ON PURPOSE. The overview uses `shouldPrompt`, which is a CADENCE
    // (the CRM re-asks every few days and a dismissal pushes it out) — right for an unprompted
    // ask. A locked page uses the plain fact that preferences are missing: keying it on the
    // cadence would leave it silent for days after any dismissal, which is the opposite of what
    // was asked for.
    (preferencesShouldPrompt || onLockedPage);

  // The membership page opens by itself for a Basic member, once per sign-in: a fresh login (or the
  // redirect straight after account creation) mints a new `loginAt`, and the one we last showed is
  // remembered here. A refresh or moving between dashboard tabs does NOT bring it back — that would
  // be nagging, not an offer.
  React.useEffect(() => {
    if (!locked || preview) return;
    // 🔴 Preferences come first, so say nothing AND remember nothing while they are on screen.
    // Stamping SHOWN_KEY here would spend this login's single showing on a popup that never
    // appeared, and the member would reach the dashboard having been offered nothing at all.
    if (prefsOpen) return;
    const key = String(loginAt ?? "no-login-stamp");
    let alreadyShown = false;
    try {
      alreadyShown = window.localStorage.getItem(SHOWN_KEY) === key;
    } catch {
      // Storage blocked (private window): show it on this page load rather than never.
    }
    if (alreadyShown) return;
    try { window.localStorage.setItem(SHOWN_KEY, key); } catch {}
    setPricingOpen(true);
  }, [locked, preview, loginAt, prefsOpen]);

  // Lock background (dashboard) scroll while a popup is open so only the modal scrolls.
  // prefsOpen counts: it is a modal too, so the scroll lock and every "wait your turn" notice
  // (the sample-data disclaimer, the live-dashboard welcome) must see it. They already read
  // `upsellOpen`, so including it here fixes them with no edit of their own.
  const anyOpen = prefsOpen || (locked && (pricingOpen || identityOpen));
  React.useEffect(() => {
    if (!anyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [anyOpen]);

  const openPricing = React.useCallback(() => {
    setIdentityOpen(false);
    setPricingOpen(true);
  }, []);

  /** Reopen the preferences popup — the FeatureLock CTA, after a dismissal. */
  const openPreferences = React.useCallback(() => setDismissedFor(null), []);

  const value = React.useMemo<GateValue>(
    () => ({ locked, openPricing, upsellOpen: anyOpen, openPreferences, preferencesMissing }),
    [locked, openPricing, anyOpen, openPreferences, preferencesMissing],
  );

  return (
    <MembershipGateContext.Provider value={value}>
      {children}

      {/* Asked BEFORE the plan offer, and on every dashboard route — including the locked ones,
          which is what makes "ask again when they try a locked item" possible at all. */}
      {prefsOpen && (
        <PreferencesModal
          services={config.serviceGroups.map((g) => ({ group: g.label, items: g.items }))}
          onSaved={() => {
            setAnswered(true);
            // "When they fill their preferences, then they'll be asked if they want to upgrade"
            // (client). Only for a Basic member — Premium has nothing to be offered.
            if (locked) setPricingOpen(true);
          }}
          onClose={() => setDismissedFor(pathname)}
        />
      )}

      {locked && !prefsOpen && pricingOpen && (
        <PricingModal
          config={config}
          selected={plan}
          onSelect={setPlan}
          onChoose={(p) => {
            setPlan(p);
            setPricingOpen(false);
            setIdentityOpen(true);
          }}
          onClose={() => setPricingOpen(false)}
        />
      )}

      {locked && !prefsOpen && identityOpen && (
        <IdentityConsentModal
          config={config}
          plan={plan}
          variant={variant}
          memberName={memberName}
          onBack={() => {
            setIdentityOpen(false);
            setPricingOpen(true);
          }}
          onClose={() => setIdentityOpen(false)}
        />
      )}
    </MembershipGateContext.Provider>
  );
}
